import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
} from '@mui/material';
import { deleteDashboard } from '../store/dashboardSlice';
import { showSnackbar } from '../store/snackbarSlice';

function DeleteDashboardModal({ open, onClose, dashboard, onSuccess }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const activeDashboard = useSelector(state => state.dashboard.activeDashboard);

  const handleClose = () => {
    setIsSubmitting(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      await dispatch(deleteDashboard(dashboard.dashboard_id)).unwrap();
      
      // Show success message
      dispatch(showSnackbar({
        message: 'Dashboard deleted successfully',
        severity: 'success'
      }));

      // If we're deleting the active dashboard, navigate to home
      if (activeDashboard && activeDashboard.dashboard_id === dashboard.dashboard_id) {
        navigate('/');
      }

      // Call the success callback if provided
      if (onSuccess) {
        onSuccess();
      }
      
      handleClose();
    } catch (error) {
      dispatch(showSnackbar({
        message: error.message || 'Failed to delete dashboard',
        severity: 'error'
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      aria-labelledby="delete-dialog-title"
      aria-describedby="delete-dialog-description"
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.3)', // slightly darker for delete
        }
      }}
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '20px',
          background: 'rgb(255, 255, 255, 0.9)',
          backdropFilter: 'blur(6px)',
          WebkitBackdropFilter: 'blur(6px)',
          border: '2px solid rgba(200, 200, 200, 0.3)',
          boxShadow: '0 8px 30px rgba(0, 0, 0, 0.08)',
        },
      }}
    >
      <DialogTitle id="delete-dialog-title" sx={{ marginTop: '10px', padding: '10px 24px' }}>
        Delete Dashboard?
      </DialogTitle>
      <DialogContent sx={{ paddingBottom: '5px' }}>
        <Typography id="delete-dialog-description">
          Are you sure you want to delete this dashboard? This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ marginBottom: '10px', marginRight: '17px' }}>
        <Button 
          onClick={handleClose} 
          disabled={isSubmitting}
          sx={{
            borderRadius: '12px',
            background: 'rgba(255, 255, 255, 0.4)',
            border: '1px solid rgba(150, 150, 150, 0.3)',
            color: '#71717A',
            cursor: 'pointer',
            transition: '0.3s ease',
            backdropFilter: 'blur(6px)',
            textTransform: 'none',
            width: '100px',
            '&:hover': {
                background: 'rgba(255, 255, 255, 0.6)',
                borderColor: 'rgba(150, 150, 150, 0.4)',
                boxShadow: '0 0 10px rgba(18, 157, 232, 0.15)',
            },
          }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained" 
          color="error"
          disabled={isSubmitting}
          sx={{
            borderRadius: '12px',
            background: 'rgba(211, 47, 47, 0.1)', // red background
            boxShadow: 'none',
            border: '1px solid rgba(211, 47, 47, 0.2)', // red border
            color: '#71717A',
            cursor: 'pointer',
            transition: '0.3s ease',
            backdropFilter: 'blur(6px)',
            textTransform: 'none',
            width: '100px',
            '&:hover': {
                background: 'rgba(211, 47, 47, 0.2)', // darker red on hover
                borderColor: 'rgba(211, 47, 47, 0.5)',
                boxShadow: '0 0 10px rgba(211, 47, 47, 0.15)', // red glow
            },
          }}
        >
          {isSubmitting ? 'Deleting...' : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default DeleteDashboardModal; 